import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Search } from 'lucide-react';
import { startOfMonth, endOfMonth } from 'date-fns';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MonthPicker } from '@/components/ui/month-picker';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  formatPhoneNumber,
  formatNumber,
  formatPaymentMethod,
} from '@/lib/utils';
import {
  calculateMultiCoursePaymentsByMonth,
} from '@/lib/payment-history';

function GroupPaymentHistory({ students, group, courses, groups }) {
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState(
    startOfMonth(new Date())
  );
  const [searchTerm, setSearchTerm] = useState('');

  // Filter students for current group
  const groupStudents = useMemo(() => {
    if (!students || !group) return [];
    return students.filter((student) =>
      group.students?.includes(student.id)
    );
  }, [students, group]);

  // Calculate payment data for each student for selected month
  const paymentData = useMemo(() => {
    if (!groupStudents || groupStudents.length === 0) return [];

    return groupStudents.map((student) => {
      const paymentInfo = calculateMultiCoursePaymentsByMonth(
        student,
        courses,
        groups,
        selectedMonth
      );

      // Find payment info for current group
      const currentGroupPayment = paymentInfo.courseStatuses.find(
        (cs) => cs.groupId === group.id
      );

      // Get payment method from payment history for this group and month
      const monthStart = startOfMonth(selectedMonth);
      const monthEnd = endOfMonth(selectedMonth);
      
      const groupPayments = (student.paymentHistory || []).filter((p) => {
        let paymentDate = null;
        if (p?.timestamp?.seconds) {
          paymentDate = new Date(p.timestamp.seconds * 1000);
        } else if (typeof p?.timestamp === 'number') {
          paymentDate = new Date(p.timestamp);
        }

        if (!paymentDate) return false;

        const inMonth = paymentDate >= monthStart && paymentDate <= monthEnd;
        const sameGroup = p.course === group.id;
        return inMonth && sameGroup;
      });

      const paymentMethod = groupPayments.length > 0 
        ? groupPayments[0].method 
        : null;

      return {
        id: student.id,
        fullName: student.fullName,
        phoneNumber: student.phoneNumber,
        paymentStatus: currentGroupPayment?.status || 'unpaid',
        paidAmount: currentGroupPayment?.paidAmount || 0,
        paymentMethod: paymentMethod,
        student: student,
      };
    });
  }, [groupStudents, group, courses, groups, selectedMonth]);

  // Filter by search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return paymentData;
    
    return paymentData.filter((item) =>
      item.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.phoneNumber?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [paymentData, searchTerm]);

  const columns = [
    {
      id: 'rowNumber',
      header: '#',
      cell: ({ row }) => (
        <div className="whitespace-nowrap">{row.index + 1}</div>
      ),
    },
    {
      accessorKey: 'fullName',
      header: 'Ism familiya',
      cell: ({ row }) => (
        <Button
          variant="link"
          className="pl-0 h-auto font-normal text-primary hover:underline"
          onClick={() => navigate(`/students/${row.original.id}`)}
        >
          {row.getValue('fullName')}
        </Button>
      ),
    },
    {
      accessorKey: 'phoneNumber',
      header: 'Telefon raqami',
      cell: ({ row }) => {
        const value = row.getValue('phoneNumber');
        return (
          <div className="whitespace-nowrap">
            {value ? formatPhoneNumber(value) : 'Raqam kiritilmagan'}
          </div>
        );
      },
    },
    {
      accessorKey: 'paymentStatus',
      header: "To'lov holati",
      cell: ({ row }) => {
        const status = row.getValue('paymentStatus');
        const statusLabels = {
          paid: "To'langan",
          partial: 'Qisman to\'langan',
          unpaid: "To'lanmagan",
          no_price: 'Narx yo\'q',
        };
        const statusColors = {
          paid: 'text-green-500',
          partial: 'text-yellow-500',
          unpaid: 'text-red-500',
          no_price: 'text-muted-foreground',
        };
        return (
          <div className={`whitespace-nowrap ${statusColors[status] || 'text-muted-foreground'}`}>
            {statusLabels[status] || status}
          </div>
        );
      },
    },
    {
      accessorKey: 'paidAmount',
      header: "To'lagan summa",
      cell: ({ row }) => {
        const amount = row.getValue('paidAmount');
        return (
          <div className="whitespace-nowrap">
            {formatNumber(amount)} so'm
          </div>
        );
      },
    },
    {
      accessorKey: 'paymentMethod',
      header: "To'lov turi",
      cell: ({ row }) => {
        const method = row.getValue('paymentMethod');
        return (
          <div className="whitespace-nowrap">
            {method ? formatPaymentMethod(method) : '-'}
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Sahifani ochish',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/students/${row.original.id}`)}
          className="h-8 w-8"
        >
          <Eye className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div className="w-full space-y-2 !pt-2">
      {/* Search and Month Picker */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="relative w-full sm:w-64">
          <Input
            placeholder="Qidirish..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="peer pe-9 ps-9"
          />
          <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 text-muted-foreground/80 peer-disabled:opacity-50">
            <Search size={16} strokeWidth={2} />
          </div>
        </div>
        <MonthPicker month={selectedMonth} setMonth={setSelectedMonth} />
      </div>

      {/* Table */}
      <div className="border rounded-md overflow-x-auto">
        <Table className="min-w-[800px]">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead className="whitespace-nowrap" key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-16 text-center"
                >
                  Ma'lumot topilmadi
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
        <div className="flex-1 text-sm text-muted-foreground text-center sm:text-left">
          {table.getFilteredRowModel().rows.length} ta natija
        </div>
        <div className="flex w-full sm:w-auto justify-center sm:justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full sm:w-auto"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Orqaga
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="w-full sm:w-auto"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Oldinga
          </Button>
        </div>
      </div>
    </div>
  );
}

export default GroupPaymentHistory;

